import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Square, Timer } from 'lucide-react';
import { Button } from '@proerp/ui';
import { useToasts } from '@/lib/toast';
import { elapsedSeconds, formatDuration } from '@/features/time-tracking/duration';
import { useRunningTimer, useStopTimer } from '@/features/time-tracking/timeTrackingApi';

/** Auf-/zugeklappt merken (geräteweit, analog den Sidebar-Gruppen). '1' = eingeklappt. */
const COLLAPSED_KEY = 'proerp.floating-timer.collapsed.v1';
function loadCollapsed(): boolean {
  try {
    return localStorage.getItem(COLLAPSED_KEY) === '1';
  } catch {
    return false;
  }
}

/**
 * Schwebender Timer — auf jeder Seite sichtbar (in der AppShell montiert, außerhalb
 * des Outlets, damit er Routenwechsel überlebt). Zeigt das laufende Projekt und die
 * sekündlich aktualisierte Dauer; Stoppen direkt von hier. Ein-/ausklappbar (merkt
 * sich den Zustand geräteweit), damit er nicht dauerhaft Inhalte darunter verdeckt.
 */
export function FloatingTimer() {
  const { data } = useRunningTimer();
  const stopTimer = useStopTimer();
  const push = useToasts((state) => state.push);
  const location = useLocation();
  const running = data?.data ?? null;
  const [now, setNow] = useState(() => Date.now());
  const [collapsed, setCollapsed] = useState(loadCollapsed);

  // Sekundentakt nur, solange ein Timer läuft.
  useEffect(() => {
    if (!running) return;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [running]);

  if (!running || !running.startTime) return null;
  // Auf dem Zeiterfassungs-Dashboard zeigt der Timer-Hero die laufende Zeit — kein Doppel.
  if (location.pathname === '/zeiterfassung') return null;

  const seconds = elapsedSeconds(running.startTime, now);
  const projectName = running.ttProjectName ?? running.projectName;
  const subtitleParts = [running.legalEntityName, running.ttSubCategoryName].filter(Boolean);

  function toggleCollapsed() {
    setCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(COLLAPSED_KEY, next ? '1' : '0');
      } catch {
        // Merken ist best effort — der State hält den Zustand auch ohne Storage.
      }
      return next;
    });
  }

  async function stop() {
    try {
      await stopTimer.mutateAsync({});
      push('success', 'Timer gestoppt.');
    } catch (error) {
      push('danger', error instanceof Error ? error.message : 'Timer konnte nicht gestoppt werden.');
    }
  }

  if (collapsed) {
    return (
      <div className="fixed bottom-16 right-4 z-50 md:bottom-4">
        <button
          type="button"
          onClick={toggleCollapsed}
          aria-label={`Timer ausklappen — ${projectName ?? 'Timer läuft'}, ${formatDuration(seconds)}`}
          className="flex items-center gap-2 rounded-lg border border-border bg-card px-3 py-2 shadow-lg hover:bg-muted/50"
        >
          <span className="flex size-8 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
            <Timer className="size-4 animate-pulse" strokeWidth={1.75} aria-hidden />
          </span>
          <span className="shrink-0 tabular-nums text-sm font-semibold text-foreground">{formatDuration(seconds)}</span>
          <ChevronLeft className="size-4 shrink-0 text-muted-foreground" strokeWidth={1.5} aria-hidden />
        </button>
      </div>
    );
  }

  return (
    <div className="fixed bottom-16 right-4 z-50 md:bottom-4">
      <div className="flex items-center gap-3 rounded-lg border border-border bg-card px-3 py-2 shadow-lg">
        <span className="flex size-8 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
          <Timer className="size-4 animate-pulse" strokeWidth={1.75} aria-hidden />
        </span>
        <div className="min-w-0">
          <p className="truncate text-sm font-medium text-foreground">{projectName ?? 'Timer läuft'}</p>
          {subtitleParts.length > 0 && (
            <p className="truncate text-xs text-muted-foreground">{subtitleParts.join(' · ')}</p>
          )}
        </div>
        <span className="shrink-0 tabular-nums text-sm font-semibold text-foreground" aria-live="off">
          {formatDuration(seconds)}
        </span>
        <Button
          variant="danger"
          size="sm"
          className="shrink-0"
          isLoading={stopTimer.isPending}
          onClick={stop}
          aria-label="Timer stoppen"
        >
          <Square className="h-4 w-4" strokeWidth={1.5} />
          Stopp
        </Button>
        <button
          type="button"
          onClick={toggleCollapsed}
          aria-label="Timer einklappen"
          className="shrink-0 text-muted-foreground hover:text-foreground"
        >
          <ChevronRight className="size-4" strokeWidth={1.5} aria-hidden />
        </button>
      </div>
    </div>
  );
}
